import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';
import { AutoRefillService } from '../../../services/auto-refill.service';
import { SharedService } from '../../../services/shared.service';
import {
  buildScheduleFreq,
  historyStatusClass,
  historyStatusLabel,
  parseScheduleFreqForm,
  pickDto,
  pickScheduleId
} from './auto-refill.utils';
import { AUTO_REFILL_FREQ_PRESETS } from './auto-refill.constants';

/** SweetAlert2 dialogs for Auto Refill. */
@Injectable({
  providedIn: 'root'
})
export class AutoRefillDialogService {

  constructor(
    private autoRefillService: AutoRefillService,
    private sharedService: SharedService
  ) { }

  openConfirm(row: any, onCompleted?: () => void): void {
    var userId = this.sharedService.getUserId();
    var scheduleId = pickScheduleId(row);
    if (!userId || scheduleId <= 0) {
      return;
    }

    var self = this;
    var brandName = String(pickDto(row, 'brandName', 'BrandName') || '');

    this.showLoadingSwal('Loading...');

    this.autoRefillService.getCycleOptions(userId, scheduleId).subscribe(
      function (opts) {
        Swal.close();
        var cycleOptions = opts || [];
        if (cycleOptions.length === 0) {
          self.showSwalError('Could not load cycle dates.');
          return;
        }
        self.showConfirmRefillDialog(row, brandName, cycleOptions, onCompleted);
      },
      function () {
        Swal.close();
        self.showSwalError('Could not load cycle dates.');
      }
    );
  }

  openDeactivate(row: any, onCompleted?: () => void): void {
    var brandName = String(pickDto(row, 'brandName', 'BrandName') || '');
    var self = this;

    Swal.fire({
      icon: 'warning',
      title: 'Confirm Deactivation',
      html:
        '<p>Deactivate schedule for <strong>' + this.escapeHtml(brandName) + '</strong>?</p>' +
        '<p class="text-muted small mb-0">This removes the medication from active auto refills.</p>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, Deactivate',
      confirmButtonColor: '#dc3545'
    }).then(function (result) {
      if (result.value) {
        self.submitDeactivate(row, onCompleted);
      }
    });
  }

  openEditSchedule(row: any, onCompleted?: () => void): void {
    var scheduleId = pickScheduleId(row);
    var userId = this.sharedService.getUserId();
    if (!userId || scheduleId <= 0) {
      return;
    }

    var self = this;
    var brandName = String(pickDto(row, 'brandName', 'BrandName') || '');
    var currentSchedule = String(pickDto(row, 'schedule', 'Schedule') || '');
    var currentNotes = String(pickDto(row, 'notes', 'Notes') || '');
    var freqForm = parseScheduleFreqForm(currentSchedule);
    var freqOptionsHtml = this.buildFreqOptionsHtml(freqForm.preset);
    var customWrapDisplay = freqForm.preset === 'Custom' ? 'block' : 'none';

    Swal.fire({
      title: 'Edit Schedule',
      width: 520,
      html:
        '<p class="mb-2 text-left">Update schedule for <strong>' + this.escapeHtml(brandName) + '</strong></p>' +
        '<label class="small font-weight-bold d-block text-left mb-1" for="arEditFreq">Frequency</label>' +
        '<select id="arEditFreq" class="swal2-input" style="width:100%;margin:0 0 0.75rem;box-sizing:border-box;">' +
        freqOptionsHtml + '</select>' +
        '<div id="arEditCustomWrap" style="display:' + customWrapDisplay + ';margin-bottom:0.75rem;">' +
        '<label class="small font-weight-bold d-block text-left mb-1" for="arEditCustomDays">Custom days</label>' +
        '<input id="arEditCustomDays" type="number" min="1" max="365" class="swal2-input" ' +
        'style="width:100%;margin:0;box-sizing:border-box;" value="' + freqForm.customDays + '" />' +
        '</div>' +
        '<label class="small font-weight-bold d-block text-left mb-1" for="arEditNotes">Notes</label>' +
        '<textarea id="arEditNotes" class="swal2-textarea" rows="3" ' +
        'style="width:100%;margin:0;box-sizing:border-box;resize:vertical;"></textarea>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Save',
      focusConfirm: false,
      onOpen: function () {
        var notesEl = document.getElementById('arEditNotes') as HTMLTextAreaElement;
        if (notesEl) {
          notesEl.value = currentNotes;
        }
        var freqEl = document.getElementById('arEditFreq') as HTMLSelectElement;
        var customWrap = document.getElementById('arEditCustomWrap');
        if (freqEl && customWrap) {
          freqEl.addEventListener('change', function () {
            customWrap.style.display = freqEl.value === 'Custom' ? 'block' : 'none';
          });
        }
      },
      preConfirm: function () {
        var preset = self.readEditFreqPreset();
        var customDays = self.readEditCustomDays(freqForm.customDays);
        if (preset === 'Custom' && customDays <= 0) {
          Swal.showValidationMessage('Please enter valid custom days.');
          return false;
        }
        var notesEl = document.getElementById('arEditNotes') as HTMLTextAreaElement;
        var notes = notesEl ? String(notesEl.value || '').trim() : '';
        return {
          scheduleFreq: buildScheduleFreq(preset, customDays),
          notes: notes
        };
      }
    }).then(function (result) {
      if (result.value) {
        self.submitUpdateSchedule(scheduleId, result.value.scheduleFreq, result.value.notes, onCompleted);
      }
    });
  }

  openHistory(row: any): void {
    var userId = this.sharedService.getUserId();
    var scheduleId = pickScheduleId(row);
    if (!userId || scheduleId <= 0) {
      return;
    }

    var self = this;
    var brandName = String(pickDto(row, 'brandName', 'BrandName') || '');
    var schedule = String(pickDto(row, 'schedule', 'Schedule') || '');
    var firstFill = String(pickDto(row, 'firstFill', 'FirstFill') || '');

    this.showLoadingSwal('Loading...');

    this.autoRefillService.getRefillHistory(userId, scheduleId).subscribe(
      function (data) {
        Swal.close();
        var historyRows = data || [];
        Swal.fire({
          title: 'Refill History',
          width: 720,
          html: self.buildHistoryHtml(brandName, schedule, firstFill, historyRows),
          confirmButtonText: 'Close'
        });
      },
      function () {
        Swal.close();
        self.showSwalError('Could not load history.');
      }
    );
  }

  private showConfirmRefillDialog(
    row: any,
    brandName: string,
    cycleOptions: any[],
    onCompleted?: () => void
  ): void {
    var self = this;
    var optionsHtml = this.buildCycleOptionsHtml(cycleOptions);
    var defaultCycle = String(pickDto(cycleOptions[0], 'value', 'Value') || '');

    var btnBaseStyle =
      'display:block;width:100%;margin:0;padding:0.65em 0.85em;font-size:0.9em;' +
      'font-weight:600;line-height:1.35;color:#fff;border:none;border-radius:0.25em;' +
      'cursor:pointer;box-shadow:0 0 0 1px rgba(0,0,0,0.05);text-align:center;';

    Swal.fire({
      title: 'Confirm Refill',
      icon: 'question',
      width: 520,
      customClass: {
        popup: 'ar-confirm-refill-swal'
      },
      html:
        '<p class="mb-2 text-left">Was the refill successful for <strong>' +
        this.escapeHtml(brandName) + '</strong>?</p>' +
        '<label class="small font-weight-bold d-block text-left mb-1" for="arSwalCycle">' +
        'Fill completed up to cycle</label>' +
        '<select id="arSwalCycle" class="swal2-input" style="width:100%;margin:0 0 1rem;box-sizing:border-box;">' +
        optionsHtml + '</select>' +
        '<div id="arSwalActions" style="display:flex;flex-direction:column;gap:0.5rem;width:100%;">' +
        '<button type="button" id="arSwalYes" style="' + btnBaseStyle + 'background-color:#28a745;">' +
        'Yes, Refilled Successful</button>' +
        '<button type="button" id="arSwalNo" style="' + btnBaseStyle + 'background-color:#dc3545;">' +
        'No, not able to Refill</button>' +
        '<button type="button" id="arSwalCancel" style="' + btnBaseStyle + 'background-color:#6c757d;">' +
        'Cancel (leave as is)</button>' +
        '</div>',
      showConfirmButton: false,
      showCancelButton: false,
      focusConfirm: false,
      onOpen: function () {
        var yesBtn = document.getElementById('arSwalYes');
        var noBtn = document.getElementById('arSwalNo');
        var cancelBtn = document.getElementById('arSwalCancel');

        if (yesBtn) {
          yesBtn.addEventListener('click', function () {
            var cycle = self.readSelectedCycle(defaultCycle);
            if (!cycle) {
              Swal.showValidationMessage('Please select a cycle.');
              return;
            }
            Swal.close();
            self.submitCompleteRefill(row, cycle, onCompleted);
          });
        }

        if (noBtn) {
          noBtn.addEventListener('click', function () {
            var cycle = self.readSelectedCycle(defaultCycle);
            if (!cycle) {
              Swal.showValidationMessage('Please select a cycle.');
              return;
            }
            Swal.close();
            self.showNotAdministeredDialog(row, cycle, onCompleted);
          });
        }

        if (cancelBtn) {
          cancelBtn.addEventListener('click', function () {
            Swal.close();
          });
        }
      }
    });
  }

  private showNotAdministeredDialog(row: any, cycleValue: string, onCompleted?: () => void): void {
    var self = this;

    Swal.fire({
      title: 'No, not able to Refill & Reason',
      width: 560,
      customClass: {
        popup: 'ar-fail-refill-reason-swal'
      },
      input: 'textarea',
      inputPlaceholder: 'Ex: Patient in hospital...',
      inputAttributes: {
        rows: '3'
      },
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Save Note',
      inputValidator: function (value) {
        if (!value || !String(value).trim()) {
          return 'Please write a reason.';
        }
        return null;
      }
    }).then(function (result) {
      if (result.value) {
        self.submitFailRefill(row, cycleValue, String(result.value).trim(), onCompleted);
      }
    });
  }

  private submitCompleteRefill(row: any, cycleValue: string, onCompleted?: () => void): void {
    var userId = this.sharedService.getUserId();
    var scheduleId = pickScheduleId(row);
    var body = {
      scheduleId: scheduleId,
      startDate: pickDto(row, 'nextFillRaw', 'NextFillRaw'),
      completedDate: cycleValue
    };
    var self = this;

    this.showLoadingSwal('Saving...');

    this.autoRefillService.completeRefill(userId, body).subscribe(
      function (res) {
        Swal.close();
        if (res && res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Saved',
            text: 'Refill marked as administered.',
            confirmButtonText: 'OK'
          });
          self.notifyCompleted(onCompleted);
          return;
        }
        self.showSwalError((res && res.message) ? res.message : 'Could not save.');
      },
      function () {
        Swal.close();
        self.showSwalError('Could not save administered refills.');
      }
    );
  }

  private submitFailRefill(
    row: any,
    cycleValue: string,
    note: string,
    onCompleted?: () => void
  ): void {
    var userId = this.sharedService.getUserId();
    var scheduleId = pickScheduleId(row);
    var body = {
      scheduleId: scheduleId,
      note: note,
      startDate: pickDto(row, 'nextFillRaw', 'NextFillRaw'),
      completedDate: cycleValue
    };
    var self = this;

    this.showLoadingSwal('Saving...');

    this.autoRefillService.failRefill(userId, body).subscribe(
      function (res) {
        Swal.close();
        if (res && res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Saved',
            text: 'Failure note recorded.',
            confirmButtonText: 'OK'
          });
          self.notifyCompleted(onCompleted);
          return;
        }
        self.showSwalError((res && res.message) ? res.message : 'Could not save.');
      },
      function () {
        Swal.close();
        self.showSwalError('Could not save failure note.');
      }
    );
  }

  private submitUpdateSchedule(
    scheduleId: number,
    scheduleFreq: string,
    notes: string,
    onCompleted?: () => void
  ): void {
    var userId = this.sharedService.getUserId();
    var body = {
      scheduleId: scheduleId,
      scheduleFreq: scheduleFreq,
      notes: notes
    };
    var self = this;

    this.showLoadingSwal('Saving...');

    this.autoRefillService.updateSchedule(userId, body).subscribe(
      function (res) {
        Swal.close();
        if (res && (res.success || res.Success)) {
          Swal.fire({
            icon: 'success',
            title: 'Saved',
            text: 'Schedule updated successfully.',
            confirmButtonText: 'OK'
          });
          self.notifyCompleted(onCompleted);
          return;
        }
        self.showSwalError((res && res.message) ? res.message : 'Could not update schedule.');
      },
      function () {
        Swal.close();
        self.showSwalError('Could not update schedule.');
      }
    );
  }

  private submitDeactivate(row: any, onCompleted?: () => void): void {
    var userId = this.sharedService.getUserId();
    var scheduleId = pickScheduleId(row);
    var self = this;

    this.showLoadingSwal('Deactivating...');

    this.autoRefillService.deactivateSchedule(userId, scheduleId).subscribe(
      function (res) {
        Swal.close();
        if (res && res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Deactivated',
            text: 'Schedule removed from active auto refills.',
            confirmButtonText: 'OK'
          });
          self.notifyCompleted(onCompleted);
          return;
        }
        self.showSwalError((res && res.message) ? res.message : 'Could not deactivate.');
      },
      function () {
        Swal.close();
        self.showSwalError('Could not deactivate schedule.');
      }
    );
  }

  private notifyCompleted(onCompleted?: () => void): void {
    if (onCompleted) {
      onCompleted();
    }
  }

  private showLoadingSwal(title: string): void {
    Swal.fire({
      title: title,
      allowOutsideClick: false,
      onOpen: function () {
        Swal.showLoading();
      }
    });
  }

  private readSelectedCycle(fallback: string): string {
    var select = document.getElementById('arSwalCycle') as HTMLSelectElement;
    if (select && select.value) {
      return select.value;
    }
    return fallback;
  }

  private buildCycleOptionsHtml(cycleOptions: any[]): string {
    var html = '';
    var i = 0;
    for (i = 0; i < cycleOptions.length; i++) {
      var value = String(pickDto(cycleOptions[i], 'value', 'Value') || '');
      var label = String(pickDto(cycleOptions[i], 'label', 'Label') || value);
      html += '<option value="' + this.escapeHtml(value) + '">' + this.escapeHtml(label) + '</option>';
    }
    return html;
  }

  private buildFreqOptionsHtml(selectedPreset: string): string {
    var html = '';
    var i = 0;
    for (i = 0; i < AUTO_REFILL_FREQ_PRESETS.length; i++) {
      var preset = AUTO_REFILL_FREQ_PRESETS[i];
      var selected = preset.id === selectedPreset ? ' selected' : '';
      html += '<option value="' + this.escapeHtml(preset.id) + '"' + selected + '>' +
        this.escapeHtml(preset.label) + '</option>';
    }
    return html;
  }

  private readEditFreqPreset(): string {
    var select = document.getElementById('arEditFreq') as HTMLSelectElement;
    if (select && select.value) {
      return select.value;
    }
    return 'Q1D (Daily)';
  }

  private readEditCustomDays(fallback: number): number {
    var input = document.getElementById('arEditCustomDays') as HTMLInputElement;
    if (!input || !input.value) {
      return fallback;
    }
    var days = parseInt(String(input.value), 10);
    if (isNaN(days)) {
      return fallback;
    }
    return days;
  }

  private buildHistoryHtml(brandName: string, schedule: string, firstFill: string, rows: any[]): string {
    var header =
      '<p class="text-left mb-2"><strong>' + this.escapeHtml(brandName) + '</strong>' +
      ' — ' + this.escapeHtml(schedule) + ' | 1st Fill: ' + this.escapeHtml(firstFill) + '</p>';

    if (!rows || rows.length === 0) {
      return header + '<p class="text-muted mb-0">No history recorded yet.</p>';
    }

    var html =
      header +
      '<div style="max-height:360px;overflow:auto;text-align:left;">' +
      '<table class="table table-hover table-striped table-sm align-middle mb-0">' +
      '<thead class="table-light"><tr>' +
      '<th>Fill Date</th><th>Status</th><th>Note</th><th>Recorded At</th>' +
      '</tr></thead><tbody>';

    var i = 0;
    for (i = 0; i < rows.length; i++) {
      var historyRow = rows[i];
      var statusClass = historyStatusClass(historyRow);
      html +=
        '<tr>' +
        '<td>' + this.escapeHtml(String(pickDto(historyRow, 'fillDate', 'FillDate') || '')) + '</td>' +
        '<td class="' + statusClass + '">' + this.escapeHtml(historyStatusLabel(historyRow)) + '</td>' +
        '<td>' + this.escapeHtml(String(pickDto(historyRow, 'note', 'Note') || '')) + '</td>' +
        '<td>' + this.escapeHtml(String(pickDto(historyRow, 'recordedAt', 'RecordedAt') || '')) + '</td>' +
        '</tr>';
    }

    html += '</tbody></table></div>';
    return html;
  }

  private showSwalError(message: string): void {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: message,
      confirmButtonText: 'OK'
    });
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
